import React, { ReactElement } from "react";

import { Alert, InputAdornment, Stack, Typography } from "@mui/material";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";

import MultiValueTextFieldGroup from "../../../../components/MultiValueTextFieldGroup";
import BasicSelector, {
  SelectorOption,
} from "../../../../components/Redux/BasicSelector";
import { ReduxTextField } from "../../../../components/Redux/TextField";
import SettingSection from "../../../../components/SettingSection";
import {
  getPercentageSettingsFormProperty,
  getSettingsFormProperty,
  setPercentageSettingsFormProperty,
  setSettingsFormError,
  setSettingsFormProperty,
} from "../../../../slices/settings";
import { useDispatch, useSelector } from "../../../../store";
import { key_value_pair } from "../../../../validators/keyValuePair";
import { tracing_sample_rate } from "../../../../validators/tracingSample";
import { validHttpUrl } from "../../../../validators/url";
import { SectionProps } from "../../../routes/components/edit_sections/common";
import { TracingOptions, TracingOtlpProtocolOptions } from "../TracingOptions";

export const Tracing = ({
  readonly,
  tabValue,
  refresh,
}: SectionProps): ReactElement => {
  const dispatch = useDispatch();
  const otelTracesExporter = useSelector(
    (state) => state.settings.form?.otelTracesExporter,
  );
  const otelExporterOtlpTracesEndpoint = useSelector(
    (state) => state.settings.form?.otelExporterOtlpEndpoint,
  );
  const otelExporterOtlpTracesHeaders = useSelector(
    (state) => state.settings.form?.otelExporterOtlpTracesHeaders,
  );

  function protocolFromOtlpEndpoint(tracingOtlpEndpoint: string) {
    try {
      const url = new URL(tracingOtlpEndpoint);
      if (url.port === "4318") {
        return "http/protobuf";
      }
      if (url.port === "4317") {
        return "grpc";
      }
    } catch {
      // ignore
    }
    return "";
  }

  function buildOtlpProtocolOptions(): SelectorOption[] {
    if (otelExporterOtlpTracesEndpoint) {
      const detected = protocolFromOtlpEndpoint(otelExporterOtlpTracesEndpoint);
      if (detected) {
        const options = structuredClone(TracingOtlpProtocolOptions);
        for (const [i, entry] of options.entries()) {
          if (entry.id === "") {
            options[i].label = `${detected} (auto-detected)`;
          }
        }
        return options;
      }
    }
    return TracingOtlpProtocolOptions;
  }

  return (
    <Container component={Paper} maxWidth={false} disableGutters={true}>
      <Stack key={tabValue + refresh} spacing={1} sx={{ m: 3 }}>
        <Typography id={tabValue} variant="h2" sx={{ pt: 2 }}>
          Tracing
        </Typography>
        <Divider />
        <SettingSection label="Tracing Provider" docsKey="tracing-provider">
          <BasicSelector
            path="otelTracesExporter"
            options={TracingOptions}
            setter={setSettingsFormProperty}
            getter={getSettingsFormProperty}
            errorHandler={setSettingsFormError}
            readonly={readonly}
          />
        </SettingSection>

        {otelTracesExporter === "otlp" && (
          <>
            <Divider />
            <SettingSection
              label="OTLP Endpoint"
              docsKey="tracing-otlp-endpoint"
            >
              <ReduxTextField
                disabled={readonly}
                setter={setSettingsFormProperty}
                getter={getSettingsFormProperty}
                errorHandler={setSettingsFormError}
                path="otelExporterOtlpTracesEndpoint"
                fullWidth
                validator={validHttpUrl}
              />
            </SettingSection>
            <Divider />
            <SettingSection
              label="OTLP Protocol"
              docsKey="tracing-otlp-protocol"
            >
              <BasicSelector
                readonly={readonly}
                options={buildOtlpProtocolOptions()}
                setter={setSettingsFormProperty}
                getter={getSettingsFormProperty}
                errorHandler={setSettingsFormError}
                path="otelExporterOtlpTracesProtocol"
              />
            </SettingSection>
            <Divider />

            <SettingSection
              label="Tracing Sample Rate"
              docsKey="tracing-sample-rate"
            >
              <ReduxTextField
                disabled={readonly}
                setter={setPercentageSettingsFormProperty}
                getter={getPercentageSettingsFormProperty}
                errorHandler={setSettingsFormError}
                path="otelTracesSamplerArg"
                validator={tracing_sample_rate}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
            </SettingSection>
            <Divider />

            <SettingSection
              label="OTLP Export Headers"
              docsKey="tracing-headers"
            >
              <MultiValueTextFieldGroup
                disabled={readonly}
                values={otelExporterOtlpTracesHeaders || []}
                onSave={(values) =>
                  dispatch(
                    setSettingsFormProperty({
                      path: "otelExporterOtlpTracesHeaders",
                      value: values.length === 0 ? undefined : values,
                    }),
                  )
                }
                validator={key_value_pair}
              />
            </SettingSection>
          </>
        )}

        {(otelTracesExporter === "datadog" ||
          otelTracesExporter === "jaeger" ||
          otelTracesExporter === "zipkin") && (
          <Alert severity="warning">
            This tracing provider has been removed; please migrate to OTLP
            (OpenTelemetry)
          </Alert>
        )}
      </Stack>
    </Container>
  );
};
